// LT-624 (LT-623): "Data to capture" schema editor.
//
// Campaign-config editor for `output_schema` — the ordered list of
// capture-field definitions the post-call analyser (LT-625) extracts per
// call and the campaign CSV export (LT-627) renders as columns. Modelled
// on the KPIs editor (CampaignGoalsKpis.tsx): a dynamic field builder with
// add / remove / reorder, wired the same way into the campaign form.
//
// Mirrors backend services/campaign_output_schema.py — keys are snake_case,
// `select` carries options, `list` carries one level of scalar item_fields.
import React from 'react';
import { ChevronDown, ChevronUp, Plus, Trash2 } from 'lucide-react';

import { Button, Card, IconButton, Input, Select, Textarea } from '../../ui';
import type {
  CampaignFieldDef,
  CampaignFieldType,
  CampaignScalarFieldType,
} from '../../types/campaign';

export const FIELD_TYPE_OPTIONS: CampaignFieldType[] = [
  'text',
  'number',
  'boolean',
  'select',
  'date',
  'list',
];

// Sub-fields of a `list` field are scalar only — no nested list.
export const SCALAR_FIELD_TYPE_OPTIONS: CampaignScalarFieldType[] = [
  'text',
  'number',
  'boolean',
  'select',
  'date',
];

export const FIELD_TYPE_LABELS: Record<CampaignFieldType, string> = {
  text: 'Text',
  number: 'Number',
  boolean: 'Yes / No',
  select: 'Select',
  date: 'Date',
  list: 'List (table)',
};

export const MAX_CAPTURE_FIELDS = 20;

/** snake_case a label for use as a field key (matches the backend's
 * ^[a-z][a-z0-9_]*$ rule). Leading digits are prefixed so the key stays
 * valid even when the label starts with a number. */
export function toSnakeCaseKey(label: string): string {
  const slug = label
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
  if (!slug) return '';
  return /^[a-z]/.test(slug) ? slug : `f_${slug}`;
}

/** Normalise a raw JSONB array into the typed shape; drop malformed entries.
 * Mirrors normalizeKpis — defensive against historical / partial data. */
export function normalizeOutputSchema(raw: unknown): CampaignFieldDef[] {
  if (!Array.isArray(raw)) return [];
  const out: CampaignFieldDef[] = [];
  for (const entry of raw) {
    const field = normalizeField(entry, true);
    if (field) out.push(field);
  }
  return out;
}

function normalizeField(entry: unknown, allowList: boolean): CampaignFieldDef | null {
  if (!entry || typeof entry !== 'object') return null;
  const obj = entry as Record<string, unknown>;
  const key = typeof obj.key === 'string' ? obj.key.trim() : '';
  const label = typeof obj.label === 'string' ? obj.label.trim() : '';
  let type =
    typeof obj.type === 'string' && FIELD_TYPE_OPTIONS.includes(obj.type as CampaignFieldType)
      ? (obj.type as CampaignFieldType)
      : 'text';
  if (type === 'list' && !allowList) type = 'text';
  if (!key && !label) return null;
  const field: CampaignFieldDef = {
    key,
    label,
    type,
    description: typeof obj.description === 'string' ? obj.description : null,
    required: Boolean(obj.required),
  };
  if (type === 'select') {
    field.options = Array.isArray(obj.options)
      ? obj.options.filter((o): o is string => typeof o === 'string')
      : [];
  }
  if (type === 'list') {
    field.item_fields = Array.isArray(obj.item_fields)
      ? obj.item_fields
          .map((f) => normalizeField(f, false))
          .filter((f): f is CampaignFieldDef => f !== null)
      : [];
  }
  return field;
}

interface CampaignOutputSchemaEditorProps {
  schema: CampaignFieldDef[];
  disabled?: boolean;
  onSchemaChange: (next: CampaignFieldDef[]) => void;
  // LT-663: the same dynamic field-def editor drives both the "data to
  // capture" (output) and "data to provide" (input) schemas — only the
  // heading copy differs. Defaults preserve the original LT-624 output copy.
  title?: string;
  description?: string;
  // Optional leading icon + accent for the section header, so the input and
  // output schema sections are visually distinct at a glance.
  icon?: React.ReactNode;
  accentClass?: string;
}

export const CampaignOutputSchemaEditor: React.FC<CampaignOutputSchemaEditorProps> = ({
  schema,
  disabled,
  onSchemaChange,
  title = 'Data to capture',
  description = 'Declare the fields the post-call analyser should extract from each call. Each becomes a column in the campaign CSV export.',
  icon,
  accentClass = 'text-slate-300',
}) => {
  const addField = () => {
    if (schema.length >= MAX_CAPTURE_FIELDS) return;
    onSchemaChange([
      ...schema,
      { key: '', label: '', type: 'text', description: '', required: false },
    ]);
  };

  const updateField = (index: number, patch: Partial<CampaignFieldDef>) => {
    onSchemaChange(
      schema.map((f, i) => {
        if (i !== index) return f;
        const next: CampaignFieldDef = { ...f, ...patch };
        // Keep the key in lock-step with the label until the operator edits
        // the key directly. (If the key already diverges from the slug of the
        // old label we treat it as operator-edited and leave it alone.)
        if (patch.label !== undefined && patch.key === undefined) {
          if (!f.key || f.key === toSnakeCaseKey(f.label)) {
            next.key = toSnakeCaseKey(patch.label);
          }
        }
        // Type switch: shed / seed the type-specific sub-config.
        if (patch.type !== undefined) {
          if (patch.type === 'select') {
            next.options = next.options ?? [];
            delete next.item_fields;
          } else if (patch.type === 'list') {
            next.item_fields = next.item_fields ?? [];
            delete next.options;
          } else {
            delete next.options;
            delete next.item_fields;
          }
        }
        return next;
      }),
    );
  };

  const removeField = (index: number) => {
    onSchemaChange(schema.filter((_, i) => i !== index));
  };

  const moveField = (index: number, dir: -1 | 1) => {
    const target = index + dir;
    if (target < 0 || target >= schema.length) return;
    const next = [...schema];
    [next[index], next[target]] = [next[target], next[index]];
    onSchemaChange(next);
  };

  return (
    <Card>
      <div className="flex items-center gap-2 mb-1">
        {icon ? <span className={accentClass}>{icon}</span> : null}
        <h3 className={`text-sm font-semibold ${accentClass}`}>{title}</h3>
      </div>
      <p className="text-xs text-slate-500 mb-3">{description}</p>

      <div className="flex flex-col gap-2">
        {schema.map((field, i) => (
          <div
            key={i}
            className="px-3 py-2 rounded-ds-md border border-white/[0.08] bg-white/[0.02]"
          >
            <div className="flex items-end gap-2">
              <label className="flex-1 block">
                <span className="text-[11px] text-slate-500 uppercase tracking-wider">Field</span>
                <Input
                  value={field.label}
                  onChange={(e) => updateField(i, { label: e.target.value })}
                  disabled={disabled}
                  placeholder="e.g. Outstanding amount"
                />
              </label>
              <label className="block w-36">
                <span className="text-[11px] text-slate-500 uppercase tracking-wider">Type</span>
                <Select
                  value={field.type}
                  onChange={(e) => updateField(i, { type: e.target.value as CampaignFieldType })}
                  disabled={disabled}
                >
                  {FIELD_TYPE_OPTIONS.map((t) => (
                    <option key={t} value={t}>{FIELD_TYPE_LABELS[t]}</option>
                  ))}
                </Select>
              </label>
              <label className="flex items-center gap-1.5 pb-2 text-xs text-slate-400 select-none whitespace-nowrap">
                <input
                  type="checkbox"
                  checked={Boolean(field.required)}
                  onChange={(e) => updateField(i, { required: e.target.checked })}
                  disabled={disabled}
                  className="accent-accent"
                />
                Required
              </label>
              <div className="flex items-center gap-1 pb-1">
                <IconButton
                  aria-label={`Move field ${i + 1} up`}
                  disabled={disabled || i === 0}
                  onClick={() => moveField(i, -1)}
                >
                  <ChevronUp size={14} />
                </IconButton>
                <IconButton
                  aria-label={`Move field ${i + 1} down`}
                  disabled={disabled || i === schema.length - 1}
                  onClick={() => moveField(i, 1)}
                >
                  <ChevronDown size={14} />
                </IconButton>
                <IconButton
                  aria-label={`Remove field ${i + 1}`}
                  disabled={disabled}
                  onClick={() => removeField(i)}
                >
                  <Trash2 size={14} />
                </IconButton>
              </div>
            </div>

            {field.type === 'select' ? (
              <OptionsEditor
                options={field.options ?? []}
                disabled={disabled}
                onChange={(opts) => updateField(i, { options: opts })}
              />
            ) : null}

            {field.type === 'list' ? (
              <ItemFieldsEditor
                itemFields={field.item_fields ?? []}
                disabled={disabled}
                onChange={(next) => updateField(i, { item_fields: next })}
              />
            ) : null}
          </div>
        ))}

        <Button
          intent="secondary"
          size="sm"
          onClick={addField}
          disabled={disabled || schema.length >= MAX_CAPTURE_FIELDS}
          className="self-start"
        >
          <Plus size={14} strokeWidth={2.5} />
          Add field
        </Button>
        {schema.length >= MAX_CAPTURE_FIELDS ? (
          <p className="text-xs text-slate-500">
            Max {MAX_CAPTURE_FIELDS} capture fields per campaign.
          </p>
        ) : null}
      </div>
    </Card>
  );
};

// ---------------------------------------------------------------------------
// select — options sub-editor
// ---------------------------------------------------------------------------

const OptionsEditor: React.FC<{
  options: string[];
  disabled?: boolean;
  onChange: (next: string[]) => void;
}> = ({ options, disabled, onChange }) => {
  const update = (index: number, value: string) =>
    onChange(options.map((o, i) => (i === index ? value : o)));
  const remove = (index: number) => onChange(options.filter((_, i) => i !== index));
  const add = () => onChange([...options, '']);

  return (
    <div className="mt-3 pl-3 border-l border-white/[0.08]">
      <span className="text-[11px] text-slate-500 uppercase tracking-wider">Options</span>
      <div className="flex flex-col gap-1.5 mt-1">
        {options.map((opt, i) => (
          <div key={i} className="flex items-center gap-2">
            <Input
              value={opt}
              onChange={(e) => update(i, e.target.value)}
              disabled={disabled}
              placeholder={`Option ${i + 1}`}
            />
            <IconButton
              aria-label={`Remove option ${i + 1}`}
              disabled={disabled}
              onClick={() => remove(i)}
            >
              <Trash2 size={14} />
            </IconButton>
          </div>
        ))}
        <Button
          intent="ghost"
          size="sm"
          onClick={add}
          disabled={disabled}
          className="self-start"
        >
          <Plus size={14} strokeWidth={2.5} />
          Add option
        </Button>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// list — item_fields sub-editor (scalar sub-fields, one level only)
// ---------------------------------------------------------------------------

const ItemFieldsEditor: React.FC<{
  itemFields: CampaignFieldDef[];
  disabled?: boolean;
  onChange: (next: CampaignFieldDef[]) => void;
}> = ({ itemFields, disabled, onChange }) => {
  const update = (index: number, patch: Partial<CampaignFieldDef>) =>
    onChange(
      itemFields.map((f, i) => {
        if (i !== index) return f;
        const next: CampaignFieldDef = { ...f, ...patch };
        if (patch.label !== undefined && patch.key === undefined) {
          if (!f.key || f.key === toSnakeCaseKey(f.label)) {
            next.key = toSnakeCaseKey(patch.label);
          }
        }
        return next;
      }),
    );
  const remove = (index: number) => onChange(itemFields.filter((_, i) => i !== index));
  const add = () =>
    onChange([...itemFields, { key: '', label: '', type: 'text' }]);

  return (
    <div className="mt-3 pl-3 border-l border-white/[0.08]">
      <span className="text-[11px] text-slate-500 uppercase tracking-wider">
        List columns
      </span>
      <div className="flex flex-col gap-1.5 mt-1">
        {itemFields.map((sub, i) => (
          <div key={i} className="flex items-end gap-2">
            <label className="flex-1 block">
              <span className="text-[10px] text-slate-500 uppercase tracking-wider">Column</span>
              <Input
                value={sub.label}
                onChange={(e) => update(i, { label: e.target.value })}
                disabled={disabled}
                placeholder="Column name"
              />
            </label>
            <label className="block w-28">
              <span className="text-[10px] text-slate-500 uppercase tracking-wider">Type</span>
              <Select
                value={sub.type}
                onChange={(e) =>
                  update(i, { type: e.target.value as CampaignScalarFieldType })
                }
                disabled={disabled}
              >
                {SCALAR_FIELD_TYPE_OPTIONS.map((t) => (
                  <option key={t} value={t}>{FIELD_TYPE_LABELS[t]}</option>
                ))}
              </Select>
            </label>
            <IconButton
              aria-label={`Remove list column ${i + 1}`}
              disabled={disabled}
              onClick={() => remove(i)}
            >
              <Trash2 size={14} />
            </IconButton>
          </div>
        ))}
        <Button
          intent="ghost"
          size="sm"
          onClick={add}
          disabled={disabled}
          className="self-start"
        >
          <Plus size={14} strokeWidth={2.5} />
          Add list column
        </Button>
      </div>
    </div>
  );
};

// Description for select sub-fields renders nothing extra — item_fields are
// intentionally minimal (key / label / type) per the LT-623 contract.
